import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import {  searchCustomer } from "src/apis/customerApi";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";


import { BRDiv } from "src/components/common/common";
import { CodeForm } from "src/components/common/form/CodeForm";
import { InputForm } from "src/components/common/form/input/InputForm";
import { SingleDateForm } from "src/components/common/form/SingleDateForm";
import { defaultPUR2010_R01HeaderDto, PUR2010_R01DetailDto, PUR2010_R01HeaderDto } from "./Dto_PUR2010_R01";
import { usePUR2010_R01Store } from "./Store_PUR2010_R01";
import { formatDate } from "src/utils/string/stringUtil";
import { SysCodeDto } from "src/dtos/code/sysCodeDto";
import { getEmployeeList } from "src/apis/employeeApi";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";



interface Props {
	editable: boolean
	defaultPUR2010_R01HeaderRow: PUR2010_R01HeaderDto;
}

export const Info_PUR2010_R01 = (props: Props) => {

	const { editable, defaultPUR2010_R01HeaderRow } = props;

	// 
	const { vatTypeList, moneyCdList, compCdList, wmsCdList } = useCommonCodeStore();
	const { setCodeSearchModalProps, setAddressModalProps } = useUiuxStore();
	const { PUR2010_R01DetailList, deletedPUR2010_R01DetailList,
			selectedPUR2010_R01,
			editPUR2010_R01, setEditPUR2010_R01,
	} = usePUR2010_R01Store();


	const custInfoHandleFoucsId = 'infoCustInput';
	const empInfoHandleFoucsId = 'infoEmpInput';
	const addressHandleFoucsId = 'addressInput2';

	// Info 폼 코드 검색 필드 설정
	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: 'customCd',
			category: '거래처',
			focusId: custInfoHandleFoucsId,
			responseMapping: {
				customCd: 'customCd',
				customNm: 'customNm',
				telNo: 'telNo',
				faxNo: 'faxNo',
				moneyCd: 'moneyCd',
				taxEmail: 'email',

			},
			searchFunction: async (searchText: string) => {
				const customerResponse = await searchCustomer(searchText);
				return customerResponse.data.data.find(value => value.customCd === searchText);
			},
			extraFilter: () => ({
				customCd: editPUR2010_R01?.customCd || '',
				customNm: editPUR2010_R01?.customNm || '',
			}),
		},
		{
			fieldName: 'empNo',
			category: '담당자',
			focusId: empInfoHandleFoucsId,
			responseMapping: {
				empNo: 'empNo',
				empNm: 'empNm'
			},
			searchFunction: async (code: string) => {
				const employeeResponse = await getEmployeeList('all');
				return employeeResponse.data.data.find(value => value.empNo === code);
			},
			extraFilter: () => ({
				empNo: editPUR2010_R01?.empNo || '',
				empNm: editPUR2010_R01?.empNm || '',
			}),
		}
	];

	// Info 폼 이벤트 훅 사용
	const { getFieldHandlers } = useInfoFormEvents({
		editData: editPUR2010_R01,
		setEditData: setEditPUR2010_R01,
		defaultData: defaultPUR2010_R01HeaderRow,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: 'PUR2010_R01'
	});

	// 출고일자 달력 팝업 상태
	const [deliveryPickerVisible, setDeliveryPickerVisible] = useState(false);
	const [deliveryPickerPos, setDeliveryPickerPos] = useState<{top: number, left: number, height: number}>({top: 0, left: 0, height: 0});

	// 매출일자 달력 팝업 상태
	const [salesPickerVisible, setSalesPickerVisible] = useState(false);
	const [salesPickerPos, setSalesPickerPos] = useState<{top: number, left: number, height: number}>({top: 0, left: 0, height: 0});

	// 부가세 입력 포커스 상태 및 표시값 관리
	const [vatAmtInputFocused, setVatAmtInputFocused] = useState(false);
	const [vatAmtInputValue, setVatAmtInputValue] = useState('');
	const [vatAmtManuallyEdited, setVatAmtManuallyEdited] = useState(false);

	useEffect(() => {
		const newPurchaseAmt = PUR2010_R01DetailList.reduce((sum, item) => sum + (deletedPUR2010_R01DetailList.includes(item.itemCd) ? 0 : item.billAmt), 0);
		
		// 조회 시에는 DB 값 그대로 사용, 편집 시에만 자동 계산 적용
		let newVatAmt = editPUR2010_R01?.vatAmt || 0;
		
		// 부가세 구분이 일반과세인지 확인 (코드명에 '일반' 또는 '과세'가 포함된 경우)
		const currentVatType = vatTypeList.find(vat => vat.minorCd === editPUR2010_R01?.vatType);
		const isGeneralTax = currentVatType?.codeNm?.includes('일반') || currentVatType?.codeNm?.includes('과세') || editPUR2010_R01?.vatType === '10';
		
		// 편집 모드이고 사용자가 부가세를 수동으로 수정하지 않은 경우에만 자동 계산
		if (isGeneralTax && !vatAmtManuallyEdited && PUR2010_R01DetailList.some(item => item.inserted || item.isModified)) {
			newVatAmt = newPurchaseAmt * 0.1; // 매출액의 10% (반올림 없음)
		} else if (vatAmtManuallyEdited) {
			// 수동 편집된 경우 기존 부가세 값 유지
			newVatAmt = editPUR2010_R01?.vatAmt || 0;
		} else {
			// 조회 시에는 기존 부가세 값 유지 (0이 아닌 경우)
			newVatAmt = editPUR2010_R01?.vatAmt || 0;
		}
		
		// 공급총액 정확히 계산
		const newTotalAmt = newPurchaseAmt + newVatAmt;
	
		setEditPUR2010_R01({
			...defaultPUR2010_R01HeaderRow,
			...editPUR2010_R01,
			purchaseAmt: newPurchaseAmt,
			vatAmt: newVatAmt,
			totalAmt: newTotalAmt
		})
	}, [PUR2010_R01DetailList, editPUR2010_R01?.vatType, vatTypeList, vatAmtManuallyEdited])

	useEffect(() => {
		// editPUR2010_R01.vatAmt가 바뀌면 표시값도 갱신 (포커스 없을 때만)
		if (!vatAmtInputFocused) {
			setVatAmtInputValue(Number(editPUR2010_R01?.vatAmt || 0).toLocaleString());
		}
	}, [editPUR2010_R01?.vatAmt, vatAmtInputFocused]);

	// 조회 시 수동 편집 상태 초기화
	useEffect(() => {
		// 조회된 데이터가 있고 편집되지 않은 상태라면 수동 편집 상태 리셋
		if (editPUR2010_R01?.purchaseBillNo && !PUR2010_R01DetailList.some(item => item.inserted || item.isModified)) {
			setVatAmtManuallyEdited(false);
		}

	}, [editPUR2010_R01?.purchaseBillNo, PUR2010_R01DetailList]);

	const handleDateChange = useCallback((field: keyof PUR2010_R01HeaderDto, value: any) => {
		setEditPUR2010_R01({
			...defaultPUR2010_R01HeaderRow,
			...editPUR2010_R01,
			[field]: value
		});
	}, [editPUR2010_R01]);

	const handleChange = useCallback((e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		// 매출액 또는 부가세가 변경될 때 공급총액도 같이 변경
		if (name === 'purchaseAmt' || name === 'vatAmt') {
			const OrderAmt = name === 'purchaseAmt' ? Number(value) : Number(editPUR2010_R01?.purchaseAmt ?? 0);
			const vatAmt = name === 'vatAmt' ? Number(value) : Number(editPUR2010_R01?.vatAmt ?? 0);
			
			// 부가세를 수동으로 수정한 경우
			if (name === 'vatAmt') {
				setVatAmtManuallyEdited(true); // 수동 편집 상태로 설정
			}
			
			setEditPUR2010_R01({
				...defaultPUR2010_R01HeaderRow,
				...editPUR2010_R01,
				[name]: Number(value), // 숫자로 변환하여 저장
				totalAmt: OrderAmt + vatAmt
			});
			return;
		}

		// 부가세 구분이 변경될 때 자동 계산
		if (name === 'vatType') {
			let newVatAmt = editPUR2010_R01?.vatAmt || 0;
			
			// 부가세 구분이 일반과세인지 확인
			const selectedVatType = vatTypeList.find(vat => vat.minorCd === value);
			const isGeneralTax = selectedVatType?.codeNm?.includes('일반') || selectedVatType?.codeNm?.includes('과세') || value === '10';
			
			// 부가세 구분이 변경되면 수동 편집 상태를 리셋하고 자동 계산
			if (isGeneralTax) {
				newVatAmt = Number(editPUR2010_R01?.purchaseAmt || 0) * 0.1; // 반올림 없음
				setVatAmtManuallyEdited(false); // 부가세 구분 변경 시 수동 편집 상태 리셋
			}
			
			setEditPUR2010_R01({
				...defaultPUR2010_R01HeaderRow,
				...editPUR2010_R01,
				[name]: value,
				vatAmt: newVatAmt,
				totalAmt: Number(editPUR2010_R01?.purchaseAmt || 0) + newVatAmt
			});
			return;
		}

		setEditPUR2010_R01({
			...defaultPUR2010_R01HeaderRow,
			...editPUR2010_R01,
			[name]: value
		});
	}, [editPUR2010_R01]);



	return (
		<div className="w-full h-full flex flex-wrap content-start pt-[12px] pl-[12px] pr-[12px] pb-[6px] overflow-y-auto border border-gray-300">
				{/* editPUR2010_R01 : {JSON.stringify(editPUR2010_R01)} <br /> */}
				<InputForm title="매입No" value={editPUR2010_R01?.purchaseBillNo || ""} 
					editable={false}
					name="purchaseBillNo" 
					onChange={handleChange} />
				<DropDownForm
					title="사업장"
					name="compCd"
					initLabelLess={true}
					value={editPUR2010_R01?.compCd}
					data={compCdList}
					editable={true}
					onChange={handleChange}
				/>
				<BRDiv />

				<SingleDateForm
					title="매입일자"
					value={editPUR2010_R01?.purchaseDt ? new Date(editPUR2010_R01.purchaseDt) : undefined}
					setValue={(value: Date | undefined) => handleDateChange('purchaseDt', formatDate(value))}
					editable={true}
					visible={salesPickerVisible}				setVisible={setSalesPickerVisible}
					onChange={handleChange}
					pickerPos={salesPickerPos}						setPickerPos={setSalesPickerPos}
				/>
				<CodeForm
					label="매입담당자"
					value={editPUR2010_R01?.empNo || ""}
					custNmValue={editPUR2010_R01?.empNm || ""}
					editable={true}
					name="empNo"
					handleChange={getFieldHandlers('empNo').onChange}
					onDoubleClick={getFieldHandlers('empNo').onDoubleClick}
					onKeyDown={getFieldHandlers('empNo').onEnterKeyDown}
				/>
				<BRDiv />


				<CodeForm
					label="거래처"
					value={editPUR2010_R01?.customCd || ""}
					custNmValue={editPUR2010_R01?.customNm || ""}
					editable={true}
					handleFoucsId={custInfoHandleFoucsId}
					name="customCd"
					handleChange={getFieldHandlers('customCd').onChange}
					onDoubleClick={getFieldHandlers('customCd').onDoubleClick}
					onKeyDown={getFieldHandlers('customCd').onEnterKeyDown}
				/>
				<DropDownForm<SysCodeDto> 
						title="기준화폐"
						data={moneyCdList}
						value={editPUR2010_R01?.moneyCd}
						name="moneyCd" 
						initLabelLess={true}
						onChange={handleChange} />
				<BRDiv />
				<BRDiv />

				
				<InputForm	title="전화번호" 
						value={editPUR2010_R01?.telNo || ""} 
						editable={true} 
						name="telNo" 
						onChange={handleChange}
					/>
				<InputForm	title="팩스번호" 
						value={editPUR2010_R01?.faxNo || ""} 
						editable={true} 
						name="faxNo" 
						onChange={handleChange}
					/>
					<BRDiv />
				
				<DropDownForm
					title="부가세구분" 
					value={editPUR2010_R01?.vatType}
					name="vatType"
					initLabelLess={true}
					data={vatTypeList}
					editable={true}
					onChange={handleChange} 
				/>
        
				<InputForm 
					title="e-mail" 
					value={editPUR2010_R01?.email || ""} 
					editable={true} 
					name="email" 
					onChange={handleChange}
				/>
				<BRDiv />

				{/* 매입담당 */}
				
				<DropDownForm<SysCodeDto> 
						title="입고물류센터"
						data={wmsCdList}
						value={editPUR2010_R01?.logicCenter || ""}
						name="logicCenter" 
						initLabelLess={true}
						onChange={handleChange} />
        
				<SingleDateForm
					title="입고일자"
					value={editPUR2010_R01?.stockInDt ? new Date(editPUR2010_R01.stockInDt) : undefined}
					setValue={(value: Date | undefined) => handleDateChange('stockInDt', formatDate(value))}
					editable={true}
					visible={deliveryPickerVisible}				setVisible={setDeliveryPickerVisible}
					pickerPos={deliveryPickerPos}				setPickerPos={setDeliveryPickerPos}
				/>
				<BRDiv />
				<InputForm	title="매입금액" 
							value={editPUR2010_R01?.purchaseAmt || 0} 
							editable={false} 
							name="purchaseAmt" 
							onChange={handleChange} 
							/>
				<BRDiv />
				
				<InputForm
					title="비고"
					value={editPUR2010_R01?.remark || ""}
					editable={true}
					name="remark"
					wrapperOption={{
						wrapperStyle: "w-[510px] min-w-[510px] mr-[10px] mb-[10px]",
						titleStyle: "w-[100px] min-w-[100px] text-left",
						inputStyle: "w-full min-w-full"
					}}
					onChange={handleChange}
				/>
				
		</div>
	)
}